/**
 * Chakrikka product gallery: set the scroll container height to the maximum of
 * all slide heights so mixed aspect ratios do not inherit only the tallest
 * slide, and crucially the scrollport height does not change when swiping
 * between slides (avoids scroll-snap jerk on WebKit/Blink).
 *
 * Updates on resize, variant change, DOM mutations, image/layout resizes
 * (ResizeObserver per slide), and initial load. Uses double rAF before layout
 * and re-anchors scrollLeft after height changes when drift exceeds epsilon.
 */
(function () {
  const ADAPTIVE_CLASS = 'product__media-list--adaptive-height';
  const REANCHOR_EPS_PX = 3;

  function getSlides(slider) {
    return Array.from(slider.querySelectorAll(':scope > li.product__media-item'));
  }

  function maxSlideHeight(slides) {
    let m = 0;
    for (const s of slides) {
      if (s.offsetParent === null) continue;
      const h = s.offsetHeight;
      if (h > m) m = h;
    }
    return m;
  }

  function reanchorScrollIfNeeded(slider, slides) {
    if (!slides.length) return;
    const sl = slider.scrollLeft;
    let bestLeft = slides[0].offsetLeft;
    let bestDist = Math.abs(bestLeft - sl);
    for (let i = 1; i < slides.length; i++) {
      const left = slides[i].offsetLeft;
      const d = Math.abs(left - sl);
      if (d < bestDist) {
        bestDist = d;
        bestLeft = left;
      }
    }
    if (bestDist <= REANCHOR_EPS_PX) return;
    if (typeof slider.scrollTo === 'function') {
      try {
        slider.scrollTo({ left: bestLeft, behavior: 'instant' });
        return;
      } catch (_) {
        /* instant not supported */
      }
    }
    slider.scrollLeft = bestLeft;
  }

  function bindGallery(component) {
    if (component.dataset.adaptiveHeightInit === 'true') return;
    const slider = component.querySelector('ul[id^="Slider-Gallery"]');
    if (!slider || !getSlides(slider).length) return;

    component.dataset.adaptiveHeightInit = 'true';

    let resizeObserver = null;
    const observedSlides = new Set();
    let rafScheduled = false;
    let lastAppliedHeight = null;

    function unobserveAllSlides() {
      if (!resizeObserver) return;
      observedSlides.forEach((el) => resizeObserver.unobserve(el));
      observedSlides.clear();
    }

    function syncSlideObservers(slides) {
      if (!window.ResizeObserver) return;
      if (!resizeObserver) {
        resizeObserver = new ResizeObserver(() => scheduleApply());
      }
      for (const el of [...observedSlides]) {
        if (!slides.includes(el)) {
          resizeObserver.unobserve(el);
          observedSlides.delete(el);
        }
      }
      slides.forEach((s) => {
        if (!observedSlides.has(s)) {
          resizeObserver.observe(s);
          observedSlides.add(s);
        }
      });
    }

    function apply() {
      slider.classList.add(ADAPTIVE_CLASS);
      const slides = getSlides(slider);
      if (!slides.length) {
        slider.style.height = '';
        unobserveAllSlides();
        lastAppliedHeight = null;
        return;
      }

      const h = maxSlideHeight(slides);
      if (h <= 0) {
        syncSlideObservers(slides);
        return;
      }

      const px = Math.ceil(h);
      const changed = lastAppliedHeight !== px;
      if (changed) {
        lastAppliedHeight = px;
        slider.style.height = `${px}px`;
        reanchorScrollIfNeeded(slider, slides);
      }
      syncSlideObservers(slides);
    }

    function scheduleApply() {
      if (rafScheduled) return;
      rafScheduled = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          rafScheduled = false;
          apply();
        });
      });
    }

    window.addEventListener('resize', scheduleApply);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, scheduleApply);
    }

    const mo = new MutationObserver(scheduleApply);
    mo.observe(slider, { childList: true });

    scheduleApply();
    window.setTimeout(scheduleApply, 100);
  }

  function init() {
    document
      .querySelectorAll('section.section-chakrikka-product .chakrikka-product slider-component[id^="GalleryViewer-"]')
      .forEach(bindGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target?.matches?.('section.section-chakrikka-product')) init();
  });
})();
